import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { PrimaryButton } from '../components/PrimaryButton';
import {
  getFaqs,
  getHelpArticles,
  type Faq,
  type HelpArticle,
} from '../../lib/site-api';
import { toast } from '../../lib/toast';

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('Account');
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  // Carousel viewport ref — we scroll it by one card-width per arrow
  // click so the responsive card widths (1/3 on desktop, 1/2 on
  // tablet, full on mobile) all just work without breakpoint math.
  const carouselRef = useRef<HTMLDivElement>(null);
  const CARDS_PER_VIEW = 3;

  const scrollCarousel = (dir: 1 | -1) => {
    const el = carouselRef.current;
    if (!el) return;
    const first = el.querySelector<HTMLElement>('[data-carousel-card]');
    const step = first ? first.offsetWidth : el.clientWidth / CARDS_PER_VIEW;
    el.scrollBy({ left: step * dir, behavior: 'smooth' });
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // FAQs power the existing accordion + Top Articles, help articles
    // power the new "Help Articles" section below the FAQs.
    Promise.all([getFaqs('CUSTOMER'), getHelpArticles('CUSTOMER')])
      .then(([faqRes, articleRes]) => {
        if (cancelled) return;
        setFaqs(faqRes.data ?? []);
        setArticles(articleRes.data ?? []);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load help content right now.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set<string>();
    faqs.forEach((f) => f.category && set.add(f.category));
    return Array.from(set).sort();
  }, [faqs]);

  const filteredFaqs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return faqs.filter((f) => {
      if (activeCategory !== 'ALL' && f.category !== activeCategory) return false;
      if (!q) return true;
      return (
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q) ||
        f.category.toLowerCase().includes(q)
      );
    });
  }, [faqs, searchQuery, activeCategory]);

  const stripHtml = (html: string) =>
    html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 pb-24">
        {/* Hero Section */}
        <section className="bg-[#f9f9f9] py-16 md:py-24 px-4">
          <div className="max-w-[700px] mx-auto text-center">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[34px] leading-[48px] tracking-[-0.094px] mb-4">
              Help Center
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] max-w-[500px] mx-auto mb-10">
              Find quick answers to common questions about bookings, payments, and your account.
            </p>

            <form
              onSubmit={handleSearchSubmit}
              className="relative max-w-[600px] mx-auto bg-white border border-[#ede8e9] rounded-[8px] h-[54px] flex items-center px-2"
            >
              <input
                type="text"
                placeholder="What are you looking for today?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 h-full bg-transparent border-none outline-none px-4 font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] placeholder:text-[#908e8e]"
              />
              <button
                type="submit"
                aria-label="Search FAQs"
                className="w-[42px] h-[40px] bg-[#8e2a2f] rounded-[6px] flex items-center justify-center shrink-0 hover:bg-[#7a2428] transition-colors"
              >
                <span className="material-icons text-white text-[18px]">search</span>
              </button>
            </form>
          </div>
        </section>

        <div
          ref={resultsRef}
          className="max-w-[1140px] mx-auto px-4 mt-16 md:mt-15 scroll-mt-24"
        >
          {loading && (
            <div className="text-center text-[#908e8e] py-12">Loading…</div>
          )}

          {!loading && (
            <section className="mb-5">
              <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[22px] leading-[32px] tracking-[-0.09px] mb-6">
                {searchQuery ? `Results for "${searchQuery}"` : 'Frequently Asked Questions'}
              </h2>

              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  <button
                    type="button"
                    onClick={() => setActiveCategory('ALL')}
                    className={`px-4 py-2 rounded-full text-[13px] font-['DM_Sans',sans-serif] font-bold border transition-colors ${activeCategory === 'ALL' ? 'bg-[#8e2a2f] text-white border-[#8e2a2f]' : 'bg-white text-[#3a3335] border-[#ede8e9] hover:border-[#8e2a2f] hover:text-[#8e2a2f]'}`}
                  >
                    All
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setActiveCategory(cat)}
                      className={`px-4 py-2 rounded-full text-[13px] font-['DM_Sans',sans-serif] font-bold border transition-colors ${activeCategory === cat ? 'bg-[#8e2a2f] text-white border-[#8e2a2f]' : 'bg-white text-[#3a3335] border-[#ede8e9] hover:border-[#8e2a2f] hover:text-[#8e2a2f]'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              {filteredFaqs.length === 0 ? (
                <p className="text-center text-[#908e8e] py-12">
                  {searchQuery || activeCategory !== 'ALL'
                    ? 'No FAQs match your filters.'
                    : 'No FAQs available yet.'}
                </p>
              ) : (
                <div className="flex flex-col border-t border-[#ede8e9]">
                  {filteredFaqs.map((f) => {
                    const isOpen = openId === f.id;
                    return (
                      <div
                        key={f.id}
                        onClick={() => setOpenId(isOpen ? null : f.id)}
                        className="border-b border-[#ede8e9] group cursor-pointer hover:bg-[#fcfaf7] transition-colors -mx-4 px-4 rounded-[8px]"
                      >
                        <div className="flex items-start gap-4 py-6">
                          <div className="w-[48px] h-[48px] bg-[#f9f9f9] rounded-[8px] flex items-center justify-center shrink-0">
                            <span className="material-icons text-[#908e8e] text-[22px]">
                              {f.icon || 'help_outline'}
                            </span>
                          </div>
                          <div className="flex-1 pt-1">
                            <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[27px] mb-1">
                              {f.question}
                            </h3>
                            <div className="flex items-center gap-2 font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
                              <span>{f.category}</span>
                            </div>
                          </div>
                          <span className="material-icons text-[#908e8e] mt-3">
                            {isOpen ? 'expand_less' : 'expand_more'}
                          </span>
                        </div>
                        {isOpen && (
                          <div
                            className="pb-6 pl-[64px] pr-4 text-[#3a3335] text-[14px] leading-[22px]"
                            dangerouslySetInnerHTML={{ __html: f.answer }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {!loading && articles.length > 0 && !searchQuery && (
            <section className="mt-24 mb-12">
              <div className="flex items-end justify-between mb-8 gap-4">
                <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[22px] leading-[32px] tracking-[-0.09px]">
                  Top Articles
                </h2>
                {/* Slider arrows — only relevant when the list overflows the
                    3 cards visible on desktop. We hide them otherwise to
                    avoid the disabled-button graveyard. */}
                {articles.length > CARDS_PER_VIEW && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      aria-label="Previous articles"
                      onClick={() => scrollCarousel(-1)}
                      className="w-10 h-10 rounded-full border border-[#ede8e9] flex items-center justify-center text-[#3a3335] hover:border-[#8e2a2f] hover:text-[#8e2a2f] transition-colors"
                    >
                      <span className="material-icons text-[20px]">
                        chevron_left
                      </span>
                    </button>
                    <button
                      type="button"
                      aria-label="Next articles"
                      onClick={() => scrollCarousel(1)}
                      className="w-10 h-10 rounded-full border border-[#ede8e9] flex items-center justify-center text-[#3a3335] hover:border-[#8e2a2f] hover:text-[#8e2a2f] transition-colors"
                    >
                      <span className="material-icons text-[20px]">
                        chevron_right
                      </span>
                    </button>
                  </div>
                )}
              </div>
              {articles.length <= CARDS_PER_VIEW ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {articles.map((a) => (
                    <ArticleCard
                      key={a.id}
                      article={a}
                      onClick={() => navigate(`/help-center/article/${a.code}`)}
                      stripHtml={stripHtml}
                    />
                  ))}
                </div>
              ) : (
                // Carousel: native scroll-snap so each breakpoint's card
                // width (full / 1-of-2 / 1-of-3) naturally aligns one
                // card per arrow tap. No JS width math needed.
                <div
                  ref={carouselRef}
                  className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-4 px-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                >
                  {articles.map((a) => (
                    <div
                      key={a.id}
                      data-carousel-card
                      className="snap-start shrink-0 w-[85%] sm:w-[calc(50%-12px)] md:w-[calc(33.333%-16px)]"
                    >
                      <ArticleCard
                        article={a}
                        onClick={() =>
                          navigate(`/help-center/article/${a.code}`)
                        }
                        stripHtml={stripHtml}
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <section className="bg-white py-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px] mb-1">
                Can't find what you need?
              </h2>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px]">
                Send us a message and we'll get back to you.
              </p>
            </div>
            <PrimaryButton
              onClick={() => navigate('/contact-us')}
              className="w-auto px-8 shrink-0 !h-[44px]"
            >
              Contact us
            </PrimaryButton>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function ArticleCard({
  article,
  onClick,
  stripHtml,
}: {
  article: HelpArticle;
  onClick: () => void;
  stripHtml: (html: string) => string;
}) {
  return (
    <div
      onClick={onClick}
      className="h-full bg-white border border-[#ede8e9] rounded-[6px] overflow-hidden flex flex-col relative group cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="h-[4px] w-full bg-[#8e2a2f] absolute top-0 left-0" />
      <div className="p-6 md:p-8 flex-1 flex flex-col">
        <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[27px] mb-4 mt-2">
          {article.title}
        </h3>
        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] mb-8 flex-1 line-clamp-3">
          {article.summary || stripHtml(article.body)}
        </p>
        <div className="flex items-center text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[12px]">
          <span className="group-hover:underline">Read more</span>
          <span className="material-icons text-[18px] ml-1">chevron_right</span>
        </div>
      </div>
    </div>
  );
}
